import React, { useEffect, useRef } from "react";
import { motion, useInView, useMotionValue, useTransform, animate } from "framer-motion";
import {
  ArrowRight, ArrowDown, Check, X, Sparkles, Activity,
  Clock, AlertTriangle, ShieldCheck, Zap, MessageSquare,
  Phone, Mail, FileText,
} from "lucide-react";

/* ─────────────────────────────────────────────────────────────────────
   The Asha blog uses a single, restrained color scheme: white/gray-50
   surfaces, gray-900 text, blue-600 / indigo-600 accents. Components
   accept an `accent` prop for back-compat but normalize everything to
   blue/indigo so the entire site reads as one design.
   ───────────────────────────────────────────────────────────────────── */

/* ─────────────────────────────────────────────────────────────────────
   Prose — opinionated typography wrapper for the long-form sections.
   ───────────────────────────────────────────────────────────────────── */
export const Prose = ({ children, className = "" }) => (
  <div
    className={
      `prose-asha text-gray-700 ` +
      `[&_h2]:text-3xl [&_h2]:md:text-[2rem] [&_h2]:font-bold [&_h2]:tracking-tight [&_h2]:text-gray-900 [&_h2]:mt-16 [&_h2]:mb-5 [&_h2]:leading-tight ` +
      `[&_h3]:text-xl [&_h3]:md:text-2xl [&_h3]:font-bold [&_h3]:text-gray-900 [&_h3]:mt-10 [&_h3]:mb-4 ` +
      `[&_h4]:text-lg [&_h4]:font-semibold [&_h4]:text-gray-900 [&_h4]:mt-6 [&_h4]:mb-2 ` +
      `[&_p]:text-[1.075rem] [&_p]:md:text-lg [&_p]:leading-[1.7] [&_p]:text-gray-700 [&_p]:mb-5 ` +
      `[&_strong]:text-gray-900 [&_strong]:font-semibold ` +
      `[&_a]:text-blue-600 [&_a]:underline [&_a]:decoration-blue-200 [&_a:hover]:decoration-blue-500 ` +
      `[&_ul]:my-5 [&_ul]:space-y-2.5 ` +
      `[&_ol]:my-5 [&_ol]:space-y-2.5 [&_ol]:list-decimal [&_ol]:list-inside ` +
      `[&_li]:text-[1.075rem] [&_li]:md:text-lg [&_li]:text-gray-700 [&_li]:leading-[1.65] ` +
      `[&_blockquote]:border-l-4 [&_blockquote]:border-blue-500 [&_blockquote]:pl-5 [&_blockquote]:py-1 [&_blockquote]:text-gray-700 [&_blockquote]:italic [&_blockquote]:my-6 ` +
      `[&_hr]:my-12 [&_hr]:border-gray-100 ` +
      `[&_code]:bg-gray-100 [&_code]:text-gray-800 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-[0.9em] ` +
      className
    }
  >
    {children}
  </div>
);

export const Lead = ({ children }) => (
  <p className="text-xl md:text-2xl text-gray-800 leading-[1.55] font-medium mb-10">
    {children}
  </p>
);

/* ─────────────────────────────────────────────────────────────────────
   PullQuote — large emphasis block.
   ───────────────────────────────────────────────────────────────────── */
export const PullQuote = ({ children, attribution }) => (
  <motion.blockquote
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.4 }}
    transition={{ duration: 0.5 }}
    className="my-12 relative pl-8 md:pl-12"
  >
    <span className="absolute left-0 top-0 text-7xl md:text-8xl leading-none font-serif text-blue-200 select-none">
      “
    </span>
    <p className="text-2xl md:text-3xl font-medium text-gray-900 leading-snug">
      {children}
    </p>
    {attribution && (
      <footer className="mt-4 text-sm text-gray-500 uppercase tracking-wider">
        — {attribution}
      </footer>
    )}
  </motion.blockquote>
);

/* ─────────────────────────────────────────────────────────────────────
   AnimatedCounter — counts from 0 to value when scrolled into view.
   ───────────────────────────────────────────────────────────────────── */
export const AnimatedCounter = ({
  value,
  prefix = "",
  suffix = "",
  duration = 1.2,
  decimals = 0,
  className = "",
}) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const motionVal = useMotionValue(0);
  const display = useTransform(motionVal, (v) =>
    `${prefix}${decimals > 0 ? v.toFixed(decimals) : Math.floor(v).toLocaleString()}${suffix}`
  );

  useEffect(() => {
    if (inView) {
      const controls = animate(motionVal, value, { duration, ease: "easeOut" });
      return () => controls.stop();
    }
  }, [inView, value, motionVal, duration]);

  return (
    <motion.span ref={ref} className={className}>
      {display}
    </motion.span>
  );
};

/* ─────────────────────────────────────────────────────────────────────
   MetricsRow — animated count-up tiles. All values render in blue.
   ───────────────────────────────────────────────────────────────────── */
export const MetricsRow = ({ items = [], className = "" }) => {
  const cols = Math.min(items.length, 4);
  const colClass =
    cols === 4 ? "md:grid-cols-4"
    : cols === 3 ? "md:grid-cols-3"
    : cols === 2 ? "md:grid-cols-2"
    : "md:grid-cols-1";
  return (
    <div className={`my-10 grid grid-cols-2 ${colClass} gap-4 md:gap-6 ${className}`}>
      {items.map((it, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.4, delay: i * 0.06 }}
          className="rounded-2xl bg-white border border-gray-200 p-5 md:p-6 shadow-sm"
        >
          <div className="text-3xl md:text-4xl font-bold tabular-nums text-blue-600">
            <AnimatedCounter
              value={it.value}
              prefix={it.prefix || ""}
              suffix={it.suffix || ""}
              decimals={it.decimals || 0}
            />
          </div>
          <div className="text-sm text-gray-500 mt-1.5 leading-snug">{it.label}</div>
        </motion.div>
      ))}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────
   BeforeAfter — two-card panel: traditional vs Asha.
   ───────────────────────────────────────────────────────────────────── */
export const BeforeAfter = ({
  beforeTitle = "The traditional way",
  afterTitle = "With Asha",
  beforeItems = [],
  afterItems = [],
}) => (
  <div className="my-12 grid grid-cols-1 md:grid-cols-2 gap-4">
    <motion.div
      initial={{ opacity: 0, x: -12 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.5 }}
      className="rounded-2xl border border-gray-200 bg-gray-50/60 p-6"
    >
      <div className="flex items-center gap-2 text-gray-500 text-xs font-bold uppercase tracking-wider mb-3">
        <X className="w-4 h-4" /> Before
      </div>
      <h3 className="text-lg font-bold text-gray-900 mb-4">{beforeTitle}</h3>
      <ul className="space-y-2.5">
        {beforeItems.map((t, i) => (
          <li key={i} className="flex items-start gap-2.5 text-gray-600">
            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />
            <span className="text-[0.95rem] leading-snug">{t}</span>
          </li>
        ))}
      </ul>
    </motion.div>
    <motion.div
      initial={{ opacity: 0, x: 12 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.5, delay: 0.1 }}
      className="rounded-2xl border border-blue-200 bg-gradient-to-b from-blue-50 to-white p-6 shadow-sm"
    >
      <div className="flex items-center gap-2 text-blue-600 text-xs font-bold uppercase tracking-wider mb-3">
        <Sparkles className="w-4 h-4" /> After
      </div>
      <h3 className="text-lg font-bold text-gray-900 mb-4">{afterTitle}</h3>
      <ul className="space-y-2.5">
        {afterItems.map((t, i) => (
          <motion.li
            key={i}
            initial={{ opacity: 0, x: 8 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.35, delay: 0.2 + i * 0.07 }}
            className="flex items-start gap-2.5 text-gray-700"
          >
            <Check className="w-4 h-4 mt-1 text-blue-600 flex-shrink-0" />
            <span className="text-[0.95rem] leading-snug">{t}</span>
          </motion.li>
        ))}
      </ul>
    </motion.div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────────
   ProcessFlow — vertical numbered steps with subtle accent line.
   ───────────────────────────────────────────────────────────────────── */
export const ProcessFlow = ({ steps = [] }) => (
  <div className="my-12 relative pl-12">
    <div className="absolute left-[19px] top-2 bottom-2 w-px bg-gradient-to-b from-blue-200 via-blue-100 to-transparent" />
    {steps.map((s, i) => (
      <motion.div
        key={i}
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.45, delay: i * 0.08 }}
        className="relative pb-8"
      >
        <div className="absolute -left-12 top-0 w-10 h-10 rounded-full bg-white border border-blue-200 shadow-sm flex items-center justify-center text-blue-700 font-bold text-sm">
          {i + 1}
        </div>
        <h4 className="text-lg font-semibold text-gray-900 mb-1.5 leading-snug">
          {s.title}
        </h4>
        <p className="text-gray-600 leading-relaxed text-[0.98rem]">{s.body}</p>
      </motion.div>
    ))}
  </div>
);

/* ─────────────────────────────────────────────────────────────────────
   ParallelGrid — grid of small cards animating in to suggest concurrency.
   No pulsing dots — uses a static blue indicator instead.
   items: [{ title, subtitle, status?: "active"|"done"|"pending", icon? }]
   ───────────────────────────────────────────────────────────────────── */
export const ParallelGrid = ({ items = [], columns = 3, title }) => {
  const colClass =
    columns === 4 ? "md:grid-cols-4" : columns === 2 ? "md:grid-cols-2" : "md:grid-cols-3";
  return (
    <div className="my-12">
      {title && (
        <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">
          {title}
        </div>
      )}
      <div className={`grid grid-cols-1 ${colClass} gap-3`}>
        {items.map((it, i) => {
          const Icon = it.icon || Activity;
          const status = it.status || "active";
          const indicator =
            status === "done" ? (
              <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
            ) : status === "pending" ? (
              <span className="h-2 w-2 rounded-full bg-gray-300 flex-shrink-0" />
            ) : (
              <span className="h-2 w-2 rounded-full bg-blue-500 flex-shrink-0" />
            );
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.3, delay: i * 0.04 }}
              className="rounded-xl border border-gray-200 bg-white p-3.5 flex items-center gap-3 shadow-sm hover:border-blue-200 transition-colors"
            >
              <div className="w-9 h-9 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0">
                <Icon className="w-4 h-4 text-blue-600" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-gray-900 truncate">
                  {it.title}
                </div>
                {it.subtitle && (
                  <div className="text-xs text-gray-500 truncate">{it.subtitle}</div>
                )}
              </div>
              {indicator}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────
   ConversationThread — animated chat-style transcript. No ping dots.
   messages: [{ from: "agent"|"patient"|"system", text, time? }]
   ───────────────────────────────────────────────────────────────────── */
export const ConversationThread = ({
  title = "Live conversation",
  channel,
  messages = [],
}) => (
  <div className="my-12 rounded-3xl border border-gray-200 bg-white shadow-sm overflow-hidden">
    <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50/60">
      <div className="flex items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-blue-500 flex-shrink-0" />
        <span className="text-sm font-semibold text-gray-700">{title}</span>
      </div>
      {channel && (
        <span className="text-xs text-gray-500 uppercase tracking-wider">{channel}</span>
      )}
    </div>
    <div className="p-5 md:p-6 space-y-3 bg-white">
      {messages.map((m, i) => {
        const isAgent = m.from === "agent";
        const isSystem = m.from === "system";
        if (isSystem) {
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.3, delay: i * 0.12 }}
              className="text-xs uppercase tracking-wider text-gray-400 text-center my-1"
            >
              {m.text}
            </motion.div>
          );
        }
        return (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.35, delay: i * 0.12 }}
            className={`flex ${isAgent ? "justify-start" : "justify-end"}`}
          >
            <div
              className={
                isAgent
                  ? "max-w-[80%] rounded-2xl rounded-bl-sm bg-blue-50 border border-blue-100 px-4 py-2.5"
                  : "max-w-[80%] rounded-2xl rounded-br-sm bg-gray-100 border border-gray-200 px-4 py-2.5"
              }
            >
              <div className={`text-[0.7rem] uppercase tracking-wider font-semibold mb-0.5 ${isAgent ? "text-blue-600" : "text-gray-500"}`}>
                {isAgent ? "Asha" : "Patient"}
              </div>
              <div className="text-[0.95rem] text-gray-800 leading-snug">
                {m.text}
              </div>
              {m.time && (
                <div className="text-[0.65rem] text-gray-400 mt-1">{m.time}</div>
              )}
            </div>
          </motion.div>
        );
      })}
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────────
   FeatureCallout — large stat panel. Single blue/indigo gradient.
   ───────────────────────────────────────────────────────────────────── */
export const FeatureCallout = ({ stat, label, description, icon: Icon = Zap }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.96 }}
    whileInView={{ opacity: 1, scale: 1 }}
    viewport={{ once: true, amount: 0.4 }}
    transition={{ duration: 0.5 }}
    className="my-12 rounded-3xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white p-8 md:p-10 relative overflow-hidden shadow-lg"
  >
    <div
      aria-hidden
      className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/10 blur-2xl"
    />
    <div
      aria-hidden
      className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-white/10 blur-2xl"
    />
    <div className="relative z-10 flex flex-col md:flex-row md:items-center gap-6">
      <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-white/15 border border-white/20 flex items-center justify-center backdrop-blur-sm">
        <Icon className="w-7 h-7" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-4xl md:text-5xl font-bold tracking-tight leading-none">
          {stat}
        </div>
        {label && (
          <div className="text-base md:text-lg text-white/85 font-medium mt-1">
            {label}
          </div>
        )}
      </div>
      {description && (
        <div className="md:max-w-xs text-white/85 text-sm md:text-base leading-relaxed border-t md:border-t-0 md:border-l border-white/20 pt-4 md:pt-0 md:pl-6">
          {description}
        </div>
      )}
    </div>
  </motion.div>
);

/* ─────────────────────────────────────────────────────────────────────
   AgentCard — agent intro card. Blue/indigo only.
   ───────────────────────────────────────────────────────────────────── */
export const AgentCard = ({
  name,
  role,
  initial,
  description,
  channels = [],
}) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.3 }}
    transition={{ duration: 0.5 }}
    className="my-10 rounded-3xl border border-blue-100 bg-gradient-to-br from-blue-50 to-indigo-50 p-6 md:p-8"
  >
    <div className="flex flex-col sm:flex-row gap-5 sm:items-center">
      <div className="flex-shrink-0 w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-3xl font-bold flex items-center justify-center shadow-lg">
        {initial || name?.[0]}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-bold uppercase tracking-wider text-blue-600 mb-1">Meet</div>
        <div className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
          {name}
        </div>
        <div className="text-base text-gray-600 mt-0.5">{role}</div>
        {description && (
          <div className="mt-3 text-gray-700 text-[0.98rem] leading-relaxed">
            {description}
          </div>
        )}
        {channels.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {channels.map((c, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 text-[0.7rem] font-semibold uppercase tracking-wider px-2 py-1 rounded-full bg-white/80 border border-blue-100 text-blue-700"
              >
                {c}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  </motion.div>
);

/* ─────────────────────────────────────────────────────────────────────
   LiveFeed — vertical activity feed. Uses a static blue dot, no ping.
   items: [{ time, label, detail, status?: "active"|"done" }]
   ───────────────────────────────────────────────────────────────────── */
export const LiveFeed = ({ title = "Live activity", items = [] }) => (
  <div className="my-12 rounded-3xl border border-gray-200 bg-white shadow-sm overflow-hidden">
    <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2 bg-gray-50/60">
      <Activity className="w-4 h-4 text-gray-500" />
      <span className="text-sm font-semibold text-gray-700">{title}</span>
    </div>
    <div className="divide-y divide-gray-100">
      {items.map((it, i) => {
        const status = it.status || "active";
        return (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -8 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.3, delay: i * 0.06 }}
            className="px-5 py-3 flex items-center gap-3"
          >
            <div className="w-12 text-[0.7rem] font-mono text-gray-400 flex-shrink-0">
              {it.time}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-900 truncate">{it.label}</div>
              {it.detail && (
                <div className="text-xs text-gray-500 truncate">{it.detail}</div>
              )}
            </div>
            {status === "done" ? (
              <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
            ) : (
              <span className="h-2 w-2 rounded-full bg-blue-500 flex-shrink-0" />
            )}
          </motion.div>
        );
      })}
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────────
   FlowDiagram — A → B → C → D, horizontal on desktop, vertical on mobile.
   ───────────────────────────────────────────────────────────────────── */
export const FlowDiagram = ({ nodes = [], title }) => (
  <div className="my-12">
    {title && (
      <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">
        {title}
      </div>
    )}
    <div className="flex flex-col md:flex-row md:items-stretch gap-3 md:gap-2">
      {nodes.map((n, i) => {
        const Icon = n.icon || Activity;
        return (
          <React.Fragment key={i}>
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.35, delay: i * 0.1 }}
              className="flex-1 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm flex items-center gap-3"
            >
              <div className="w-9 h-9 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0">
                <Icon className="w-4 h-4 text-blue-600" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-gray-900 leading-tight">
                  {n.label}
                </div>
                {n.sublabel && (
                  <div className="text-xs text-gray-500 truncate mt-0.5">
                    {n.sublabel}
                  </div>
                )}
              </div>
            </motion.div>
            {i < nodes.length - 1 && (
              <div className="flex md:items-center justify-center text-blue-300">
                <ArrowDown className="md:hidden w-5 h-5" />
                <ArrowRight className="hidden md:block w-5 h-5" />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────────
   StatBar — horizontal bar chart with animated width.
   ───────────────────────────────────────────────────────────────────── */
export const StatBar = ({ rows = [], title }) => (
  <div className="my-12 rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
    {title && (
      <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-4">
        {title}
      </div>
    )}
    <div className="space-y-4">
      {rows.map((r, i) => {
        const pct = Math.min(100, Math.round((r.value / r.max) * 100));
        return (
          <div key={i}>
            <div className="flex items-baseline justify-between text-sm mb-1.5">
              <span className="text-gray-700 font-medium">{r.label}</span>
              <span className="text-gray-900 font-bold tabular-nums">
                {r.value.toLocaleString()}
                {r.suffix || ""}
              </span>
            </div>
            <div className="relative h-2.5 rounded-full bg-gray-100 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: `${pct}%` }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.9, delay: i * 0.08, ease: "easeOut" }}
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full"
              />
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────────
   TimelineList — vertical timestamped event list.
   events: [{ time, channel, label, by? }]
   ───────────────────────────────────────────────────────────────────── */
export const TimelineList = ({ events = [], title = "Patient timeline" }) => (
  <div className="my-12 rounded-3xl border border-gray-200 bg-white shadow-sm overflow-hidden">
    <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2 bg-gray-50/60">
      <Clock className="w-4 h-4 text-gray-500" />
      <span className="text-sm font-semibold text-gray-700">{title}</span>
    </div>
    <div className="relative px-6 py-5">
      <div className="absolute left-[34px] top-5 bottom-5 w-px bg-gray-200" />
      {events.map((e, i) => {
        const ChannelIcon = e.channel === "sms" ? MessageSquare
          : e.channel === "email" ? Mail
          : e.channel === "call" ? Phone
          : e.channel === "form" ? FileText
          : Activity;
        return (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -8 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.3, delay: i * 0.07 }}
            className="relative flex items-start gap-4 py-2.5"
          >
            <div className="z-10 w-10 h-10 rounded-full bg-white border border-blue-200 shadow-sm flex items-center justify-center flex-shrink-0">
              <ChannelIcon className="w-4 h-4 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-gray-400 font-mono">{e.time}</div>
              <div className="text-sm font-medium text-gray-900 leading-snug">
                {e.label}
              </div>
              {e.by && (
                <div className="text-xs text-gray-500 mt-0.5">{e.by}</div>
              )}
            </div>
          </motion.div>
        );
      })}
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────────
   ChecklistCard — checks running / passing / failing.
   Status icons remain semantic (pass=blue check, fail=amber warning,
   running=spinner) but everything else uses the blue scheme.
   ───────────────────────────────────────────────────────────────────── */
export const ChecklistCard = ({ items = [], title }) => (
  <div className="my-12 rounded-3xl border border-gray-200 bg-white shadow-sm overflow-hidden">
    {title && (
      <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2 bg-gray-50/60">
        <ShieldCheck className="w-4 h-4 text-gray-500" />
        <span className="text-sm font-semibold text-gray-700">{title}</span>
      </div>
    )}
    <div className="p-5 space-y-2.5">
      {items.map((it, i) => {
        const icon = it.status === "pass"
          ? <Check className="w-4 h-4 text-blue-600" />
          : it.status === "fail"
          ? <AlertTriangle className="w-4 h-4 text-amber-600" />
          : <span className="w-4 h-4 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />;
        const bg = it.status === "fail"
          ? "bg-amber-50/60 border-amber-100"
          : "bg-blue-50/40 border-blue-100";
        return (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -8 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.3, delay: i * 0.07 }}
            className={`rounded-xl border ${bg} px-4 py-2.5 flex items-center gap-3`}
          >
            <div className="flex-shrink-0">{icon}</div>
            <div className="text-sm text-gray-800 flex-1">{it.label}</div>
          </motion.div>
        );
      })}
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────────
   SectionDivider — small section break.
   ───────────────────────────────────────────────────────────────────── */
export const SectionDivider = ({ label }) => (
  <div className="my-16 flex items-center gap-4">
    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
    {label && (
      <span className="text-xs uppercase tracking-[0.2em] text-gray-400 font-semibold">
        {label}
      </span>
    )}
    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
  </div>
);

const exports = {
  Prose, Lead, PullQuote, AnimatedCounter, MetricsRow, BeforeAfter,
  ProcessFlow, ParallelGrid, ConversationThread, FeatureCallout, AgentCard,
  LiveFeed, FlowDiagram, StatBar, TimelineList, ChecklistCard, SectionDivider,
};
export default exports;
